import React, { useState } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  useTheme,
} from "@mui/material";
import {
  Email,
  Phone,
  LocationOn,
  Send,
  AccountBalance,
  SmartToy,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import Layout from "../components/layout/LandingPageLayout";
import config from "../config.js";

const ContactUs = () => {
  const [submitStatus, setSubmitStatus] = useState(null);
  const {API_BASE_URL}=config
  const [isSubmitting, setIsSubmitting] = useState(false);
  const theme = useTheme();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      email: "",
      company: "",
      phone: "",
      subject: "",
      message: "",
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/contactus`,
        {
          name: data.name,
          email: data.email,
          company: data.company,
          phone: data.phone,
          subject: data.subject,
          message: data.message,
          timestamp: new Date().toISOString(),
          source: "contact-form",
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 30000,
        }
      );

     

      setSubmitStatus({
        type: "success",
        message:
          "Thank you for contacting F-AI! We'll get back to you within 24 hours.",
      });

      reset();
    } catch (error) {
      console.error("Contact form submission error:", error);

      let errorMessage =
        "Something went wrong. Please try again or contact us directly.";

      if (error.response) {
        if (error.response.status === 400) {
          errorMessage = "Please check your input and try again.";
        } else if (error.response.status === 500) {
          errorMessage = "Server error. Please try again later.";
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        errorMessage =
          "Network error. Please check your connection and try again.";
      }

      setSubmitStatus({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: theme.palette.background.default,
          py: 4,
        }}
      >
        <Container maxWidth="lg">
          {/* Header Section */}
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 2,
              }}
            >
              <SmartToy
                sx={{ fontSize: 40, color: theme.palette.primary.main, mr: 1 }}
              />
              <Typography
                variant="h3"
                sx={{ fontWeight: 300, color: theme.palette.primary.main }}
              >
                f-ai
              </Typography>
            </Box>
            <Typography variant="h4" gutterBottom>
              Contact Our AI Accounting Assistant
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: "auto" }}
            >
              Have questions about our AI-powered accounting solutions? We're
              here to help streamline your financial processes.
            </Typography>
            <Box
              sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2 }}
            >
              <Chip
                icon={<AccountBalance />}
                label="AI Accounting"
                color="primary"
              />
              <Chip
                icon={<SmartToy />}
                label="Smart Assistance"
                color="primary"
                variant="outlined"
              />
            </Box>
          </Box>

          <Grid container spacing={4}>
            {/* Contact Form */}
            <Grid item xs={12} md={8}>
              <Paper elevation={3} sx={{ p: 4 }}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ color: theme.palette.primary.main }}
                >
                  Get in Touch
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {submitStatus && (
                  <Alert
                    severity={submitStatus.type}
                    sx={{ mb: 3 }}
                    onClose={() => setSubmitStatus(null)}
                  >
                    {submitStatus.message}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="name"
                        control={control}
                        rules={{ required: "Name is required" }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Full Name"
                            error={!!errors.name}
                            helperText={errors.name?.message}
                            variant="outlined"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="email"
                        control={control}
                        rules={{
                          required: "Email is required",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address",
                          },
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Email Address"
                            type="email"
                            error={!!errors.email}
                            helperText={errors.email?.message}
                            variant="outlined"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="company"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Company Name"
                            variant="outlined"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="phone"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Phone Number"
                            variant="outlined"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Controller
                        name="subject"
                        control={control}
                        rules={{ required: "Subject is required" }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Subject"
                            error={!!errors.subject}
                            helperText={errors.subject?.message}
                            variant="outlined"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Controller
                        name="message"
                        control={control}
                        rules={{ required: "Message is required" }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Message"
                            multiline
                            rows={4}
                            error={!!errors.message}
                            helperText={errors.message?.message}
                            variant="outlined"
                            placeholder="Tell us about your accounting needs or questions..."
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        startIcon={
                          isSubmitting ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Send />
                          )
                        }
                        disabled={isSubmitting}
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          "&:hover": {
                            backgroundColor: theme.palette.primary.dark,
                          },
                          py: 1.5,
                          px: 4,
                        }}
                      >
                        {isSubmitting ? "Sending..." : "Send Message"}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12} md={4}>
              <Paper elevation={3} sx={{ p: 4, height: "fit-content" }}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ color: theme.palette.primary.main }}
                >
                  Contact Information
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Email sx={{ color: theme.palette.primary.main, mr: 2 }} />
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body2">info@f-ai.in</Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Phone sx={{ color: theme.palette.primary.main, mr: 2 }} />
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography variant="body2">+91 -9452701180</Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <LocationOn
                      sx={{ color: theme.palette.primary.main, mr: 2 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Address
                      </Typography>
                      <Typography variant="body2">
                        Thubarahalli, Whitefield,
                        <br />
                        Bangalore, India
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box>
                  <Typography variant="h6" gutterBottom>
                    Why Choose F-AI?
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Our AI-powered accounting assistant helps businesses
                    automate financial processes, reduce errors, and gain
                    valuable insights.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • 24/7 AI Support
                    <br />
                    • Automated Bookkeeping
                    <br />
                    • Financial Analytics
                    <br />• Tax Compliance
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Layout>
  );
};

export default ContactUs;
